/* ===================================================================
   useSessionToken() -- bridge a Supabase login to a data-plane rr_ token.

   On a ready Supabase session, POST the user JWT (session.access_token)
   to /api/v1/auth/session-token (engine/api/v1/session_token.py). The
   backend verifies the JWT, resolves the caller's client_id, and returns
   a freshly-minted rr_ key scoped to that client. We store it via
   setToken(), which notifies the reactive token store so the data hooks
   re-enable immediately.

   On sign-out (no session), we clear the rr_ token so the next user
   doesn't inherit it.

   SAFE TO MOUNT MULTIPLE TIMES: the actual exchange is deduped at the
   MODULE level (one in-flight call per access_token, tracked in
   _exchange). So mounting this hook from each of the 3 data screens
   (Dashboard / Reviews / Customers) results in exactly one network call,
   not three -- no key churn, no revoke storm. It returns enough status
   for a screen to show "setting up your account" if the client isn't
   linked yet (409).
   =================================================================== */
import { useEffect, useState } from 'react';
import { useSession } from '../auth/useSession';
import { api, setToken, getToken, ApiError } from '../lib/api';
import { useApiToken } from './useApiToken';

/* Module-level dedupe.

   CRITICAL (token-churn fix, 2026-06-17): the backend session-token endpoint
   REVOKES any prior rr_ key every time it mints a new one. Supabase emits
   several access_tokens over a session's life (initial getSession, the
   onAuthStateChange replay, periodic refreshes), so re-exchanging on every
   access_token caused a revoke storm: token A works -> exchange mints B and
   revokes A -> in-flight queries on A 401 -> that churn re-triggers another
   exchange -> ... The Dashboard never stabilized (panels half-empty).

   The rule now: exchange ONCE to obtain an rr_ token, then REUSE it across
   access_token changes. We only re-exchange when we have NO usable token
   (fresh load with nothing persisted, or after a token was cleared by a real
   auth failure / sign-out). A refreshed Supabase access_token does NOT force a
   new rr_ key. The dedupe record is keyed on the access_token only to share
   one concurrent in-flight call; presence of a stored token short-circuits it
   entirely. */
interface ExchangeRecord {
  accessToken: string;
  promise: Promise<void>;
  state: SessionTokenState;
  error: string | null;
  clientId: string | null;
}
let _exchange: ExchangeRecord | null = null;
const _exchangeSubs = new Set<() => void>();

function _notify() {
  for (const fn of _exchangeSubs) {
    try {
      fn();
    } catch {
      /* a bad subscriber must not break the notify loop */
    }
  }
}

function _runExchange(accessToken: string): void {
  // We already hold a usable rr_ token -> REUSE it. Re-exchanging would revoke
  // it server-side and start the churn. The token is cleared (forcing a fresh
  // exchange here) only by sign-out or a real 401 (see api.ts onUnauthorized).
  if (getToken()) {
    if (!_exchange || _exchange.state !== 'ready') {
      _exchange = {
        accessToken,
        state: 'ready',
        error: null,
        clientId: null,
        promise: Promise.resolve(),
      };
      _notify();
    }
    return;
  }

  // Already handled (or in-flight) for this exact token? No-op.
  if (_exchange && _exchange.accessToken === accessToken) return;

  const rec: ExchangeRecord = {
    accessToken,
    state: 'exchanging',
    error: null,
    clientId: null,
    promise: Promise.resolve(),
  };
  rec.promise = api
    .exchangeSessionToken(accessToken)
    .then((res) => {
      if (_exchange !== rec) return; // superseded by a newer token
      setToken(res.api_key); // notifies the reactive token store
      rec.clientId = res.client_id;
      rec.state = 'ready';
      _notify();
    })
    .catch((e: unknown) => {
      if (_exchange !== rec) return;
      if (e instanceof ApiError && e.status === 409) {
        rec.state = 'not_linked';
        rec.error =
          'Your account is still being set up. This can take a moment after signup.';
      } else {
        rec.state = 'error';
        rec.error =
          e instanceof ApiError
            ? e.detail || e.message
            : e instanceof Error
              ? e.message
              : 'Could not connect to your data.';
      }
      // Allow a later retry (e.g. once the clients row is provisioned, or
      // a transient network error clears) by dropping the record.
      _exchange = null;
      _notify();
    });
  _exchange = rec;
  _notify();
}

function _clearExchange(): void {
  if (_exchange === null) return;
  _exchange = null;
  _notify();
}

export type SessionTokenState =
  | 'idle' // no session yet
  | 'exchanging' // calling the endpoint
  | 'ready' // rr_ token in hand
  | 'not_linked' // auth user has no client row yet (409)
  | 'error'; // exchange failed (network / server)

export interface UseSessionTokenResult {
  state: SessionTokenState;
  error: string | null;
  /** client_id resolved by the backend (handy for connect-GBP flows). */
  clientId: string | null;
}

export function useSessionToken(): UseSessionTokenResult {
  const { session, loading } = useSession();
  const token = useApiToken();
  // Re-render this hook whenever the module-level exchange record changes.
  const [, force] = useState(0);

  const accessToken = session?.access_token ?? null;

  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _exchangeSubs.add(cb);
    return () => {
      _exchangeSubs.delete(cb);
    };
  }, []);

  useEffect(() => {
    // Signed out: clear any stale rr_ token + exchange record.
    if (!loading && !accessToken) {
      if (token) setToken(null);
      _clearExchange();
      return;
    }
    if (!accessToken) return; // still loading the session
    // Exchange only when we have NO token. When a token exists, _runExchange
    // short-circuits to "ready" and reuses it (no revoke storm). `token` IS a
    // dep now so that if a 401 clears the token (api.ts onUnauthorized), this
    // re-fires and mints exactly one replacement.
    _runExchange(accessToken);
  }, [accessToken, loading, token]);

  // Derive the public result from the module record (or the token state).
  if (_exchange && _exchange.accessToken === accessToken) {
    return {
      state: _exchange.state,
      error: _exchange.error,
      clientId: _exchange.clientId,
    };
  }
  if (token) {
    return { state: 'ready', error: null, clientId: null };
  }
  return { state: accessToken ? 'exchanging' : 'idle', error: null, clientId: null };
}
